<template>
  <SideBarSideMenu
    :label="t('SideBar.festivalEvents.shop')"
    :to="{ name: 'shopBookingSelection' }"
    :side-bar-urls="getSidebarUrls(festivalShopSubMenuItems)"
    force-open
  >
    <template #prependLabel>
      <ShoppingCart class="mr-2 lucide-icon-s" />
    </template>
    <template #default>
      <p v-if="selectedLocationLabel" class="-mt-4 mb-2 pl-2 txt-body-s text-subtle">
        {{ selectedLocationLabel }}
      </p>
      <button
        v-if="hasSelectedKiosk"
        data-testid="sidebar-shop-change-kiosk"
        class="btn-outline-neutral-xs mb-6"
        @click="isKioskModalOpen = true"
      >
        {{ t('SideBar.festivalShop.changeKiosk') }}
      </button>
      <div v-if="hasSelectedKiosk" class="flex flex-col gap-1">
        <div v-if="kioskDetailsLoading" class="flex justify-center py-6">
          <CircleSpinner class="text-main" />
        </div>
        <template v-else>
          <SideBarItem
            v-if="overviewRoute"
            data-testid="sidebar-festival-shop-overview"
            :to="overviewRoute"
            :nest-level="1"
            :active-routes="overviewActiveRoutes"
            disable-router-active
            class="text-left text-subtle"
          >
            {{ t('SideBar.festivalShop.overview') }}
          </SideBarItem>
          <SideBarItem
            v-if="manageOrdersRoute"
            data-testid="sidebar-shop-orders"
            :to="manageOrdersRoute"
            :nest-level="1"
            :active-routes="manageOrdersActiveRoutes"
            disable-router-active
            class="text-left text-subtle"
          >
            {{ t('SideBar.festivalShop.orderedItems') }}
          </SideBarItem>
          <template v-if="categoryMenuItems.length">
            <p class="mt-3 ml-2 txt-body-s text-subtlest">{{ t('SideBar.festivalShop.categories') }}</p>
            <SideBarItem
              v-for="item in categoryMenuItems"
              :key="item.id"
              :to="item.disabled ? undefined : item.to"
              :nest-level="1"
              :aria-disabled="item.disabled"
              class="'text-left'"
              :class="{ 'cursor-not-allowed text-disabled': !!item.disabled }"
              :tabindex="item.disabled ? -1 : undefined"
            >
              {{ item.label }}
            </SideBarItem>
          </template>
          <p v-else class="px-3 py-3 txt-body-s text-subtle">{{ t('SideBar.festivalShop.noProducts') }}</p>
        </template>
      </div>
      <div v-else class="w-full text-center">
        <img src="/assets/images/festival-shop-nav-illustration.svg" class="mx-auto" />
        <p class="mt-4 text-subtle">{{ t('SideBar.festivalShop.emptyState') }}</p>
      </div>
    </template>
  </SideBarSideMenu>

  <ConfirmModal
    v-if="isKioskModalOpen"
    hide-actions
    wrapper-class="w-11/12 max-w-3xl !max-h-[600px] rounded-lg"
    @close-dialog="isKioskModalOpen = false"
  >
    <template #content>
      <div class="flex flex-col gap-3 px-6 pb-6">
        <p class="mb-0 p-6 pl-0 txt-body-xl-bold">{{ t('SideBar.festivalShop.changeKioskModal.title') }}</p>
        <p class="mb-8 max-w-[400px] txt-body-m text-subtle">
          {{ t('SideBar.festivalShop.changeKioskModal.description') }}
        </p>
        <p v-if="!availableKiosks.length || !bookingEvent" class="txt-body-s text-subtle">
          {{ t('SideBar.festivalShop.emptyState') }}
        </p>
        <template v-for="kiosk in availableKiosks">
          <BookingItem v-if="bookingEvent" :key="kiosk.id" :kiosk="kiosk" :event="bookingEvent" :is-past="false" />
        </template>
        <p class="max-w-[400px] txt-body-m text-subtle">
          {{ t('SideBar.festivalShop.changeKioskModal.footerQuestion') }}
          <router-link :to="{ name: 'shopBookingSelection', query: { period: 'past' } }" class="btn-link-color-m-underline">
            {{ t('SideBar.festivalShop.changeKioskModal.footerLink') }}
          </router-link>
        </p>
      </div>
    </template>
  </ConfirmModal>
</template>
<script setup lang="ts">
import { CircleSpinner } from '@ramp106/omrjs-core-ui'
import { ShoppingCart } from '@ramp106/omrjs-core-ui/icons'
import { storeToRefs } from 'pinia'
import { computed, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRoute, useRouter, type RouteLocationRaw } from 'vue-router'

import ConfirmModal from '@/components/ConfirmModal.vue'
import BookingItem from '@/components/FestivalShop/BookingItem.vue'
import { useEventBySlugQuery, useKioskByIdQuery, type EventsQuery, type KioskByIdQuery } from '@/gql/myomr'
import { toPlainId } from '@/helpers/globalIdHelper'
import { globalizeUrlifiedId } from '@/helpers/urlifyGlobalId'
import { useFestivalShopStore } from '@/stores/festivalShop'

import type { SidebarItemType } from '../SideBar.vue'

const { t } = useI18n()

const route = useRoute()
const router = useRouter()
const festivalShopStore = useFestivalShopStore()
const { availableKiosks } = storeToRefs(festivalShopStore)
const isKioskModalOpen = ref(false)

const festivalShopSubMenuItems: SidebarItemType[] = [
  {
    show: true,
    to: { name: 'festivalShop' },
    activeRoutes: ['festivalShop'],
    label: t('SideBar.booths'),
  },
]

const eventSlug = computed(() => (route.params.eventSlug as string | undefined) ?? null)
const kioskId = computed(() => (route.params.kioskId as string | undefined) ?? null)
const hasSelectedKiosk = computed(() => Boolean(eventSlug.value && kioskId.value))
const kioskGlobalId = computed(() => (kioskId.value ? globalizeUrlifiedId(kioskId.value) : ''))
const overviewActiveRoutes = ['shopBookingOverview']
const manageOrdersActiveRoutes = ['shopManageOrders', 'editOrderedProduct']

const overviewRoute = computed<RouteLocationRaw | null>(() => {
  if (!hasSelectedKiosk.value) return null
  return {
    name: 'shopBookingOverview',
    params: {
      eventSlug: eventSlug.value,
      kioskId: kioskId.value,
    },
  }
})

const manageOrdersRoute = computed<RouteLocationRaw | null>(() => {
  if (!hasSelectedKiosk.value) return null
  return {
    name: 'shopManageOrders',
    params: {
      eventSlug: eventSlug.value,
      kioskId: kioskId.value,
    },
  }
})

const eventQueryVars = computed(() => ({
  slug: eventSlug.value ?? '',
}))
const eventQueryOptions = computed(() => ({
  enabled: eventSlug.value != null,
}))
const { result: eventResult } = useEventBySlugQuery(eventQueryVars, eventQueryOptions)
const eventName = computed(() => eventResult.value?.eventBySlug?.name ?? '')
const bookingEvent = computed<NonNullable<EventsQuery['events']['nodes']>[number] | null>(() => {
  return (eventResult.value?.eventBySlug as NonNullable<EventsQuery['events']['nodes']>[number] | undefined) ?? null
})

watch(
  () => route.fullPath,
  () => {
    isKioskModalOpen.value = false
  },
)

const queryVars = computed(() => ({
  kioskId: kioskGlobalId.value,
}))
const queryOptions = computed(() => ({
  enabled: hasSelectedKiosk.value,
}))

const { result: kioskDetailsResult, loading: kioskDetailsLoading } = useKioskByIdQuery(queryVars, queryOptions)
const kioskName = computed(() => {
  const kiosk = kioskDetailsResult.value?.kioskById?.kiosk
  if (!kiosk) return ''
  if (kiosk.__typename === 'Booth') {
    return kiosk.boothNumber ?? kiosk.name ?? ''
  }
  return kiosk.name ?? ''
})
type ProductCategory = NonNullable<KioskByIdQuery['kioskById']>['productCategories'][number]
const productCategories = computed<ProductCategory[]>(() => kioskDetailsResult.value?.kioskById?.productCategories ?? [])

const selectedLocationLabel = computed(() => {
  if (!eventSlug.value) return ''
  if (kioskName.value) {
    return kioskName.value
  }
  return eventName.value || eventSlug.value
})

type CategoryMenuItem = {
  id: string
  label: string
  to: RouteLocationRaw
  disabled: boolean
}

const isCategoryExpired = (category: ProductCategory) => {
  if (!category.expiresAt) return false
  return new Date(category.expiresAt) < new Date()
}

const categoryMenuItems = computed<CategoryMenuItem[]>(() => {
  if (!hasSelectedKiosk.value) return []

  return productCategories.value
    .filter((category) => !isCategoryExpired(category))
    .map((category) => ({
      id: category.id,
      label: category.name ?? '',
      to: {
        name: 'shopProductCategoryOverview',
        params: {
          eventSlug: eventSlug.value,
          kioskId: kioskId.value,
          productCategoryId: toPlainId(category.id),
        },
      },
      disabled: isCategoryExpired(category),
    }))
})

function getSidebarUrls(items: Array<SidebarItemType>) {
  return items.map((item) => (typeof item.to === 'string' ? item.to : router.resolve(item.to).path))
}
</script>
